<template>
  <div class="diagnostic-tool">
    <h2>🔍 BBL Editor Diagnostic Tool</h2>
    <p>This tool checks if all required dependencies are properly installed.</p>
    
    <div class="diagnostic-results">
      <div class="check-section">
        <h3>Vue Composition API</h3>
        <div class="check-item" :class="{ success: checks.compositionApi, error: !checks.compositionApi }">
          <span class="icon">{{ checks.compositionApi ? '✅' : '❌' }}</span>
          <span>@vue/composition-api</span>
          <span v-if="!checks.compositionApi" class="install-cmd">npm install @vue/composition-api</span>
        </div>
      </div>

      <div class="check-section">
        <h3>Tiptap Core</h3>
        <div class="check-item" :class="{ success: checks.tiptapCore, error: !checks.tiptapCore }">
          <span class="icon">{{ checks.tiptapCore ? '✅' : '❌' }}</span>
          <span>@tiptap/core & @tiptap/vue-2</span>
          <span v-if="!checks.tiptapCore" class="install-cmd">npm install @tiptap/core @tiptap/vue-2</span>
        </div>
      </div>

      <div class="check-section">
        <h3>Tiptap Extensions</h3>
        <div v-for="(status, extension) in checks.extensions" :key="extension" 
             class="check-item" :class="{ success: status, error: !status }">
          <span class="icon">{{ status ? '✅' : '❌' }}</span>
          <span>{{ extension }}</span>
          <span v-if="!status" class="install-cmd">npm install {{ extension }}</span>
        </div>
      </div>

      <div class="check-section">
        <h3>ProseMirror Dependencies</h3>
        <div v-for="(status, dep) in checks.prosemirror" :key="dep" 
             class="check-item" :class="{ success: status, error: !status }">
          <span class="icon">{{ status ? '✅' : '❌' }}</span>
          <span>{{ dep }}</span>
          <span v-if="!status" class="install-cmd">npm install {{ dep }}</span>
        </div>
      </div>
    </div>

    <div class="summary" :class="{ success: allChecksPass, error: !allChecksPass }">
      <h3>{{ allChecksPass ? '🎉 All Dependencies Found!' : '⚠️ Missing Dependencies' }}</h3>
      <p v-if="allChecksPass">
        Your setup is complete! The BBL Editor should work properly.
      </p>
      <div v-else>
        <p>Install missing dependencies with this command:</p>
        <div class="install-all">
          <code>{{ installCommand }}</code>
          <button @click="copyToClipboard" class="copy-btn">📋 Copy</button>
        </div>
      </div>
    </div>

    <div class="test-editor" v-if="allChecksPass">
      <h3>🧪 Test Editor</h3>
      <PremiumBblEditor 
        v-model="testContent" 
        placeholder="If you can see this editor and type here, everything is working!"
        :show-toolbar="true"
        style="min-height: 150px;"
      />
    </div>
  </div>
</template>

<script>
import PremiumBblEditor from './PremiumBblEditor.vue'

export default {
  name: 'DiagnosticTool',
  components: {
    PremiumBblEditor
  },
  data() {
    return {
      testContent: '<p>Test content - you can edit this!</p>',
      checks: {
        compositionApi: false,
        tiptapCore: false,
        extensions: {},
        prosemirror: {}
      }
    }
  },
  computed: {
    allChecksPass() {
      return this.checks.compositionApi && 
             this.checks.tiptapCore && 
             Object.values(this.checks.extensions).every(Boolean) &&
             Object.values(this.checks.prosemirror).every(Boolean)
    },
    installCommand() {
      const missing = []
      
      if (!this.checks.compositionApi) missing.push('@vue/composition-api')
      if (!this.checks.tiptapCore) missing.push('@tiptap/core', '@tiptap/vue-2')
      
      Object.entries(this.checks.extensions).forEach(([ext, status]) => {
        if (!status) missing.push(ext)
      })
      
      Object.entries(this.checks.prosemirror).forEach(([dep, status]) => {
        if (!status) missing.push(dep)
      })
      
      return `npm install ${missing.join(' ')}`
    }
  },
  mounted() {
    this.runDiagnostics()
  },
  methods: {
    runDiagnostics() {
      // Check Vue Composition API
      try {
        const { ref } = require('@vue/composition-api')
        this.checks.compositionApi = typeof ref === 'function'
      } catch (e) {
        this.checks.compositionApi = false
      }

      // Check Tiptap Core
      try {
        const { Editor } = require('@tiptap/vue-2')
        this.checks.tiptapCore = !!Editor
      } catch (e) {
        this.checks.tiptapCore = false
      }

      // Check Tiptap Extensions
      const extensions = [
        '@tiptap/starter-kit',
        '@tiptap/extension-text-style',
        '@tiptap/extension-color',
        '@tiptap/extension-highlight',
        '@tiptap/extension-underline',
        '@tiptap/extension-text-align',
        '@tiptap/extension-link',
        '@tiptap/extension-font-family',
        '@tiptap/extension-code',
        '@tiptap/extension-code-block',
        '@tiptap/extension-table',
        '@tiptap/extension-table-row',
        '@tiptap/extension-table-cell',
        '@tiptap/extension-table-header',
        '@tiptap/extension-task-list',
        '@tiptap/extension-task-item',
        '@tiptap/extension-placeholder'
      ]

      extensions.forEach(ext => {
        try {
          require(ext)
          this.$set(this.checks.extensions, ext, true)
        } catch (e) {
          this.$set(this.checks.extensions, ext, false)
        }
      })

      // Check ProseMirror dependencies
      const prosemirrorDeps = [
        'prosemirror-state',
        'prosemirror-view',
        'prosemirror-model',
        'prosemirror-commands',
        'prosemirror-keymap',
        'prosemirror-history',
        'prosemirror-transform',
        'prosemirror-tables'
      ]

      prosemirrorDeps.forEach(dep => {
        try {
          require(dep)
          this.$set(this.checks.prosemirror, dep, true)
        } catch (e) {
          this.$set(this.checks.prosemirror, dep, false)
        }
      })
    },
    
    async copyToClipboard() {
      try {
        await navigator.clipboard.writeText(this.installCommand)
        alert('Command copied to clipboard!')
      } catch (err) {
        // Fallback for older browsers
        const textArea = document.createElement('textarea')
        textArea.value = this.installCommand
        document.body.appendChild(textArea)
        textArea.select()
        document.execCommand('copy')
        document.body.removeChild(textArea)
        alert('Command copied to clipboard!')
      }
    }
  }
}
</script>

<style scoped>
.diagnostic-tool {
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

.diagnostic-tool h2 {
  color: #1f2937;
  margin-bottom: 8px;
}

.diagnostic-tool p {
  color: #6b7280;
  margin-bottom: 24px;
}

.check-section {
  margin-bottom: 24px;
}

.check-section h3 {
  color: #374151;
  font-size: 16px;
  margin-bottom: 12px;
  border-bottom: 1px solid #e5e7eb;
  padding-bottom: 4px;
}

.check-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 12px;
  border-radius: 6px;
  margin-bottom: 4px;
}

.check-item.success {
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
}

.check-item.error {
  background: #fef2f2;
  border: 1px solid #fecaca;
}

.icon {
  font-size: 16px;
  width: 20px;
}

.install-cmd {
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 12px;
  background: rgba(0, 0, 0, 0.05);
  padding: 2px 6px;
  border-radius: 3px;
  margin-left: auto;
}

.summary {
  padding: 20px;
  border-radius: 8px;
  margin: 24px 0;
  text-align: center;
}

.summary.success {
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  color: #166534;
}

.summary.error {
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #991b1b;
}

.install-all {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
  padding: 12px;
  background: #f9fafb;
  border-radius: 6px;
  border: 1px solid #d1d5db;
}

.install-all code {
  flex: 1;
  background: #1f2937;
  color: #f9fafb;
  padding: 8px 12px;
  border-radius: 4px;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 12px;
  word-break: break-all;
  white-space: pre-wrap;
}

.copy-btn {
  background: #3b82f6;
  color: white;
  border: none;
  padding: 8px 12px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  white-space: nowrap;
}

.copy-btn:hover {
  background: #2563eb;
}

.test-editor {
  margin-top: 32px;
  padding: 20px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #f9fafb;
}

.test-editor h3 {
  margin-top: 0;
  color: #374151;
}
</style>